import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _startsWith from 'lodash/startsWith';
// Serialization
import { deserialize, serialize } from 'serializr';
import { deviceSlaveSchema } from './schemas';
// Permissions
import permissions from '../constants/Permissions';
// Custom
import { SEARCHFIELD_TYPES } from '../constants/App';
import { joinNonEmpty } from '../utils/Tools';

/**
* Serialization
*/
const serializerKey = 'device_slave';

const deserializeDeviceSlave = (data) => deserialize(deviceSlaveSchema, data);

const serializeDeviceSlave = ({ companyId }) => ({
  [serializerKey]: {
    ...serialize(deviceSlaveSchema, {
      ...(companyId && { company_id: companyId }),
    }),
  },
});

/**
* utils
*/
const deviceSlaveTitle = (deviceSlave) => _get(deviceSlave, 'serial', '');
const deviceSlaveInstallationDesc = (deviceSlave) => (deviceSlave ? joinNonEmpty([deviceSlave.serial, deviceSlave.name], ' - ') : '');

const deviceSlaveIsMotherboard = (deviceSlave) => {
  const deviceModel = _get(deviceSlave, 'device_model');
  if (deviceModel) {
    return !deviceModel.productible && _startsWith(deviceModel.version, 'mb_');
  }
  return false;
};

/**
* Model
*/
const deviceSlave = {
  // Name and icon
  name: 'deviceSlave',
  icon: 'deviceSlave',

  // Serialization
  deserialize: deserializeDeviceSlave,
  serialize: serializeDeviceSlave,

  // Utils
  title: deviceSlaveTitle,
  installationDesc: deviceSlaveInstallationDesc,
  isMotherboard: deviceSlaveIsMotherboard,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'serial', dir: 'asc' },
      ],
    },

    // Autocomplete filter
    autoCompleteFilter: {
      field: 'serial',
      operator: SEARCHFIELD_TYPES.string.defaultOperator(),
    },

    // Columns
    columnsQuantity: (hasPermission) => ({
      xs: 1,
      sm: 2 + (hasPermission({ needed: permissions.pAdmin }) | 0),
      md: 2 + (hasPermission({ needed: permissions.pAdmin }) | 0) * 2,
      lg: 2 + (hasPermission({ needed: permissions.pAdmin }) | 0) * 2,
      xl: 2 + (hasPermission({ needed: permissions.pAdmin }) | 0) * 4,
    }),
    columns: [
      {
        key: 'serial',
        labelId: 'field.serial',
        field: 'serial',
        sortable: true,
      },
      {
        key: 'device_model',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.deviceModel',
        field: 'device_model',
        sortable: false,
        className: 'hidden-xs-down',
      },
      {
        key: 'device',
        labelId: 'field.device',
        field: 'device_slave_maps.device.serial',
        sortable: true,
        className: 'table-content-2 hidden-xs-down',
      },
      {
        key: 'device_distribution_maps',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.distributions',
        field: 'device_slave_distribution_maps',
        sortable: false,
        className: 'hidden-sm-down',
      },
      {
        key: 'productionBatch',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.productionBatch',
        field: 'device_slave_production_batch_id',
        sortable: false,
        className: 'hidden-lg-down',
      },
      {
        key: 'company',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.company',
        field: 'company',
        sortable: false,
        className: 'hidden-lg-down',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'serial',
        labelId: 'field.serial',
        field: 'serial',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        filter: {
          field: 'serial',
        },
      },
      {
        key: 'deviceModelId',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.deviceModel',
        field: 'deviceModelId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'device_model_id',
        },
      },
      {
        key: 'available',
        labelId: 'field.deviceAttached',
        field: 'available',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.boolean,
        filter: {
          field: 'available',
        },
      },
      {
        key: 'availableForDevice',
        hidden: true,
        labelId: '',
        field: 'availableForDevice',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        excludeFilterFields: ['available', 'device'],
        type: SEARCHFIELD_TYPES.boolean,
        filter: {
          field: 'available_for_device',
        },
      },
      {
        key: 'device',
        labelId: 'field.device',
        field: 'device',
        alias: 'device_id',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'device_slave_maps.device_id',
          key: 'id',
        },
      },
      {
        key: 'distribution',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.distribution',
        field: 'distribution',
        alias: 'distribution_id',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        excludeFilterFields: ['distribution', 'device_slave_distribution_maps.distribution_id'],
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'device_slave_distribution_maps.distribution_id',
          key: 'id',
        },
      },
      {
        key: 'deviceSlaveBatchId',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.productionBatch',
        field: 'deviceSlaveBatchId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'device_slave_production_batch_id',
        },
      },
      {
        key: 'companyId',
        permissions: {
          needed: permissions.pAdmin,
        },
        labelId: 'field.company',
        field: 'companyId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'company_id',
        },
      },
    ],
  },
};

module.exports = deviceSlave;



// WEBPACK FOOTER //
// ./models/deviceSlave.js